/**
 * Drop-in replacement for `@ohear/audio` Recorder used in e2e tests.
 *
 * Activates ONLY when `window.__OHEAR_TEST_MODE__ === true`. The production
 * recorder path is untouched — see {@link createRecorder} in `useRecorder.ts`
 * for the dispatch logic.
 *
 * The mock implements the same surface that `useRecorder` consumes:
 *   - `start()`   — resolves quickly, simulates a worklet by ticking peak.
 *   - `stop()`    — returns a synthesized 48k/24-bit-target Float32 buffer.
 *   - `cancel()`  — discards in-flight buffer.
 *   - `currentPeak` — live peak; mirrors the worklet message contract.
 *
 * The synthesized PCM is a low-amplitude 440 Hz sine wave (peak ≈ 0.25)
 * that satisfies:
 *   - local QC's `peak < 0.001` empty-recording guard
 *   - server-side QC noise-floor / clipping checks (well under -3 dBFS)
 *
 * Duration is whatever wall-clock elapses between `start()` and `stop()`,
 * synthesized at 48 kHz. Caller can also force a fixed duration via
 * `__OHEAR_TEST_HOOKS__.recorderDurationMs` (number).
 */

import type { RecorderOptions, RecorderResult } from '@ohear/audio'

/** Subset of @ohear/audio Recorder that {@link useRecorder} actually uses. */
export interface RecorderLike {
  /**
   * Pre-warm the recorder. The real implementation loads the AudioWorklet
   * module and primes a MediaStream so the first `start()` doesn't pay the
   * full init cost. Optional — `start()` falls back to lazy init if this
   * isn't called. Resolves without throwing on permission errors so the UI
   * can decide how to surface them.
   */
  prewarm?(): Promise<void>
  start(): Promise<void>
  stop(): Promise<RecorderResult>
  cancel(): void
  readonly currentPeak: number
}

const DEFAULT_FREQ_HZ = 440
const DEFAULT_AMPLITUDE = 0.25 // ~ -12 dBFS — well above 0 and well below clip.
const PEAK_TICK_MS = 16 // ~60 Hz, matches the real recorder's RAF cadence.

export class MockRecorder implements RecorderLike {
  private state: 'idle' | 'recording' | 'stopped' = 'idle'
  private startedAt = 0
  private peak = 0
  private peakTimer: ReturnType<typeof setInterval> | null = null
  private prewarmed = false

  constructor(public readonly options: RecorderOptions) {
    if (options.bitDepth !== 24) {
      // Match the real recorder's contract — caller should still pass 24.
      throw new Error(`MockRecorder: bitDepth ${String(options.bitDepth)} not supported (24 only)`)
    }
  }

  /**
   * Mirror the real Recorder's prewarm. ~50ms simulated init keeps the UI
   * spinner visible for at least one frame so e2e specs see the disabled-then-
   * enabled state transition deterministically.
   */
  async prewarm(): Promise<void> {
    if (this.prewarmed) return
    await new Promise<void>((resolve) => setTimeout(resolve, 50))
    this.prewarmed = true
  }

  async start(): Promise<void> {
    if (this.state !== 'idle') {
      throw new Error(`MockRecorder.start: invalid state ${this.state}`)
    }
    this.state = 'recording'
    this.startedAt = Date.now()
    this.peak = 0
    // Simulate the worklet's per-frame peak messages by interpolating from 0
    // up to DEFAULT_AMPLITUDE over the first ~250 ms, then holding.
    this.peakTimer = setInterval(() => {
      const elapsed = Date.now() - this.startedAt
      const ramp = Math.min(1, elapsed / 250)
      this.peak = DEFAULT_AMPLITUDE * ramp
    }, PEAK_TICK_MS)
    // Resolve on next microtask so the caller's `await start()` behaves like
    // the real Recorder (which awaits getUserMedia + addModule).
    await Promise.resolve()
  }

  async stop(): Promise<RecorderResult> {
    if (this.state !== 'recording') {
      throw new Error(`MockRecorder.stop: invalid state ${this.state}`)
    }
    this.state = 'stopped'
    if (this.peakTimer) {
      clearInterval(this.peakTimer)
      this.peakTimer = null
    }

    const overrideMs = readDurationOverrideMs()
    const elapsedMs = overrideMs ?? Math.max(50, Date.now() - this.startedAt)
    const sampleRate = this.options.sampleRate
    const numSamples = Math.floor((elapsedMs / 1000) * sampleRate)
    const pcm = synthesizeSine(numSamples, sampleRate, DEFAULT_FREQ_HZ, DEFAULT_AMPLITUDE)
    return {
      pcm,
      durationSec: numSamples / sampleRate,
      peak: DEFAULT_AMPLITUDE,
    }
  }

  cancel(): void {
    if (this.state === 'idle') return
    this.state = 'idle'
    if (this.peakTimer) {
      clearInterval(this.peakTimer)
      this.peakTimer = null
    }
    this.peak = 0
  }

  get currentPeak(): number {
    return this.peak
  }
}

/**
 * Read a duration override from `window.__OHEAR_TEST_HOOKS__.recorderDurationMs`
 * when set. Lets specs deterministically force a take length without waiting
 * out wall-clock time. Returns `null` if absent or malformed.
 */
function readDurationOverrideMs(): number | null {
  if (typeof window === 'undefined') return null
  const hooks = window.__OHEAR_TEST_HOOKS__
  if (!hooks) return null
  const raw = (hooks as unknown as { recorderDurationMs?: unknown }).recorderDurationMs
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null
  return raw
}

/** Build a `numSamples`-long Float32 sine at the given freq + amplitude. */
function synthesizeSine(
  numSamples: number,
  sampleRate: number,
  freqHz: number,
  amplitude: number,
): Float32Array {
  const out = new Float32Array(numSamples)
  const w = (2 * Math.PI * freqHz) / sampleRate
  for (let i = 0; i < numSamples; i++) {
    out[i] = amplitude * Math.sin(w * i)
  }
  return out
}

/**
 * Returns true iff the SPA should substitute the mock recorder.
 * Guarded so production builds with no test flag set always return false.
 */
export function isTestMode(): boolean {
  return typeof window !== 'undefined' && window.__OHEAR_TEST_MODE__ === true
}
